/**
 * The projection behind the inspector: one `GET /plan-template/{id}/full` in, the pattern week and
 * the muscle split out.
 *
 * **The only module in this slice that names `PlanTemplateFullResponse`.** `ui/` reads
 * `InspectorView` and never the generated model — the same seam `planLibraryRow` holds for the
 * list, and the same lint rule enforces it.
 *
 * ── The pattern week is the ENTITY's ──────────────────────────────────────────────────────────
 * Which phase is drawn, which canvas the mode selects and what a day that is not there looks like
 * moved to `entities/plan-draft`'s `patternWeekOf` on 23 September 2026, when „Nowy program"'s
 * template preview became its second reader. The tests stay here, at this entry point.
 *
 * ── The OVERVIEW is the one figure a client's plan cannot read off its list row ────────────────
 * A template's hero quotes its day figure from the row the trainer pointed at, so nothing on the
 * plate waits. A CLIENT'S plan has no such row: its list row is an assignment, and
 * `PlanAssignmentSummary` carries no phases. The phase TREE the library's scalars were computed from
 * is on this response, so the day count is derived here the way the backend derives it:
 * `DaysPerWeek` is `min(densest phase's day count, 7)` — `PlanTemplateProcessor` line 777, read in
 * the C# rather than assumed.
 *
 * Until 4 November 2026 the overview also carried the span, the rhythm, the freshness and the
 * exercise count for the hero's sub-line and its first figure. The owner's polish took both off the
 * plate, and nothing else read them.
 *
 * ── The counts are VALUES, and the sentences are the screen's ────────────────────────────────
 * `t` here is `TranslateKey` — a key-only translate, which is what keeps `model/` free of
 * i18next. A count cannot travel through one, so `7 ćw.` is composed by `PatternWeek` from
 * `exerciseCount`, exactly as `ListRows` composes the row's sub-line.
 *
 * No React, no DOM, no query hook.
 */

import { patternWeekOf, type PatternWeekView } from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse as PlanTemplateFullResponse } from '~/shared/api/generated/model';

import { toMuscleSplitView, type MuscleSplitView } from './muscleSplitView';
import type { PlanRhythm, TranslateKey } from './planLibraryRow';

/** The figure a hero prints that a client's plan cannot read off its own list row. */
export interface PlanOverview {
  /**
   * Training days in the repeat unit: the densest phase's day count, capped at seven, exactly as
   * the backend computes the library's `daysPerWeek`. `null` where the plan has no phases, exactly
   * as `PlanLibraryRow.daysInCycle` is.
   *
   * It is here rather than counted off `weekTiles` at the call site because those are two different
   * questions with two different answers: the tiles draw the FIRST phase, and the rhythm is the
   * DENSEST. A hero that counted the tiles printed `3` over a plan whose second phase trains five.
   */
  readonly daysInCycle: number | null;
}

export interface InspectorView extends PatternWeekView {
  readonly muscle: MuscleSplitView;
  readonly overview: PlanOverview;
}

const DAYS_IN_WEEK = 7;

function planOverviewOf(response: PlanTemplateFullResponse): PlanOverview {
  const phases = response.phases ?? [];
  const densest = phases.reduce((most, phase) => Math.max(most, (phase.days ?? []).length), 0);
  // The row's own rule: a plan with no phases has no repeat unit to measure, and `0 dni` under a
  // figure would be a measurement rather than the absence it is.
  return { daysInCycle: phases.length > 0 ? Math.min(densest, DAYS_IN_WEEK) : null };
}

/**
 * How many days the plan's repeat unit holds — the hero's first figure.
 *
 * **One rule for one figure, and this is where it lives.** A template quotes its list row, because
 * that row arrived with the screen and states the backend's own `min(densest phase's day count, 7)`.
 * A client's plan has no such row — its list row is an assignment — so the figure comes from the
 * plan tree, through `PlanOverview`, which recomputes exactly that.
 *
 * `rowRhythm` is `null` for a client's plan, which is not a special case here so much as the honest
 * answer: an assignment carries no cadence, so there is nothing to prefer over the tree.
 *
 * A row whose rhythm is a CYCLE count rather than a week — every non-DateBased mode — carries no day
 * figure at all, so that one falls to the tree as well.
 */
export function daysInRepeatUnit(
  rowRhythm: PlanRhythm | null,
  overview: PlanOverview | null,
): number | null {
  if (rowRhythm?.kind === 'week') return rowRhythm.daysPerWeek;
  return overview?.daysInCycle ?? null;
}

export function toInspectorView(
  response: PlanTemplateFullResponse,
  t: TranslateKey,
): InspectorView {
  const muscle = toMuscleSplitView(response.muscleSplit, t);
  const overview = planOverviewOf(response);
  return { ...patternWeekOf(response), muscle, overview };
}
